/* DataExplorerGridViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./DataExplorerGridViewRenderer");
const Registrations = require("../../../../../RegistrationNamespaces");
const PagingTemplateFeed = require("./PagingTemplateFeed");
const DateColumnRenderer = require("./column_renderers/date/DateComponent");
const DefaultForRenderer = require("./column_renderers/default_for/DefaultForComponent");
const CheckboxColumnRenderer = require("./column_renderers/checkbox/CheckboxComponent");

const _ = require("lodash");

/**
 * @fires DataExplorerGridViewComponent#PathsEditingState
 */
class DataExplorerGridViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        storageFactory,
        solutionsManager,
        modalUserFeedback,
        ExplorerTemplate,
        Explorables
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = modalUserFeedback;
        this._sessionStorage = undefined;
        this._solutionsManager = solutionsManager;
        this._solutionName =
            (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "templateExplorer";
        this._explorerTemplate = ExplorerTemplate;
        this._explorables = Explorables;

        this._storageFactory = storageFactory;
        this._userGridData = undefined;

        this._osImagePaths = undefined;
        this._pagingTemplateFeed = undefined;
        this._explorerState = {
            selectedIds: [],
            selectedPathId: "",
        };
        this.columnConfig = {
            grouping: {
                label: "Group",
                width: 220,
                priority: 0,
                shown: false,
                entityColumn: true,
                isGroupingColumn: true,
            },
            name: {
                label: "Name",
                width: 220,
                priority: 1,
            },
            description: {
                label: "Description",
                width: 300,
                priority: 2,
            },
            shared: {
                label: "Inheritable",
                width: 90,
                priority: 3,
                shown: false,
                groupable: false,
                columnRendererComponent: CheckboxColumnRenderer,
            },
            private: {
                label: "Private",
                width: 90,
                priority: 3,
                shown: false,
                groupable: true,
                columnRendererComponent: CheckboxColumnRenderer,
            },
            defaultForDm: {
                label: "Default for DM",
                width: 130,
                priority: 4,
                columnRendererComponent: DefaultForRenderer,
                context: {
                    column: "defaultForDm",
                    orgId: _.get(this._userSession, "activeOrganization.id"),
                    userId: _.get(this._userSession, "user.id"),
                },
                entityColumn: true,
            },
            defaultForCda: {
                label: "Default for CDA",
                width: 130,
                priority: 5,
                columnRendererComponent: DefaultForRenderer,
                context: {
                    column: "defaultForCda",
                    orgId: _.get(this._userSession, "activeOrganization.id"),
                    userId: _.get(this._userSession, "user.id"),
                },
                entityColumn: true,
            },
            defaultForOsm: {
                label: "Default for OSM",
                width: 130,
                priority: 6,
                columnRendererComponent: DefaultForRenderer,
                context: {
                    column: "defaultForOsm",
                    orgId: _.get(this._userSession, "activeOrganization.id"),
                    userId: _.get(this._userSession, "user.id"),
                },
                entityColumn: true,
            },
            createdBy: {
                label: "Created By",
                width: 150,
                priority: 7,
            },
            createdInOrgName: {
                label: "Created In",
                width: 150,
                priority: 7,
            },
            createdOn: {
                label: "Created On",
                width: 135,
                priority: 8,
                shown: false,
                columnRendererComponent: DateColumnRenderer,
            },
            updatedBy: {
                label: "Modified By",
                width: 150,
                priority: 9,
            },
            updatedOn: {
                label: "Modified On",
                width: 135,
                priority: 10,
                columnRendererComponent: DateColumnRenderer,
            },
        };
        this._sessionStorage = undefined;
        this._registerListeners();
        this._renderer = new Renderer(this);
    }

    get pagingFeed() {
        return this._pagingTemplateFeed;
    }

    get storageFactory() {
        return this._storageFactory;
    }

    get selectedRows() {
        return this._renderer.selectedRows;
    }

    get activeOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._storageFactory.on(
            "storageReady",
            (this._onStorageReady = this._onStorageReady.bind(this))
        );
        this._gameEditor._LMRBLayout.on(
            "SelectRowsOnTemplateGrid",
            (this._onSelectRowsOnTemplateGrid = this._onSelectRowsOnTemplateGrid.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._storageFactory) {
            this._storageFactory.removeListener("storageReady", this._onStorageReady);
        }
        if (this._gameEditor && this._gameEditor._LMRBLayout) {
            this._gameEditor._LMRBLayout.removeListener(
                "SelectRowsOnTemplateGrid",
                this._onSelectRowsOnTemplateGrid
            );
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/
    whenModelIsReady() {
        return this.gameEditor
            .whenInitialized()
            .then(() => {
                this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                    "entity_grid_gl",
                    this._gridKey
                );
                this._sessionStorage = this._storageFactory.getSessionStorageInstance(
                    "entity_grid_gl",
                    `${this._gridKey}_explorerState`
                );
                this._explorerState = Object.assign(
                    {},
                    this._explorerState,
                    this._sessionStorage.getData()
                );
            })
            .then(() => {
                // const workTarget = {
                //     method: 'query',
                //     category: 'entity',
                //     entityType: 'Explorables',
                //     query: {},
                //   };

                // return this._explorables.runDiscoverExplorablesWork(this.activeOrgId, workTarget, {}, {}, (progress) => {
                //     console.log(progress);
                // });
                return this._explorables.loadExplorables(this.activeOrgId);
            })
            .then((/*data*/) => {
                // console.log(data);
                this._pagingTemplateFeed = new PagingTemplateFeed({
                    feedKey: this._gridKey + "_feed",
                    userSession: this._userSession,
                    entityDef: this._explorerTemplate,
                    columnConfig: this.columnConfig,
                    additionalDataFields: ["ownerId"],
                });
                this._registerListeners();
                return this._pagingTemplateFeed.whenFeedReady();
            });
    }

    get renderer() {
        return this._renderer;
    }

    get logger() {
        return this._logger;
    }

    shutdown() {
        this.emit("Shutdown");
        this._unregisterListeners();
        let shutdowns = [
            _.isUndefined(this._renderer) ? Promise.resolve() : this._renderer.shutdown(),
            _.isUndefined(this._pagingTemplateFeed)
                ? Promise.resolve()
                : this._pagingTemplateFeed.shutdown(),
        ];
        return Promise.all(shutdowns).then(
            () => {
                this._logger = undefined;
                this._gameEditor = undefined;
                this._steveUtils = undefined;
                this._userSession = undefined;
                this._modalUserFeedback = undefined;
                this._sessionStorage = undefined;
                this._solutionsManager = undefined;
                this._solutionName = undefined;

                //this.columns = undefined;
                this._sessionStorage = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "DataExplorerGridViewComponent.shutdown() error");
            }
        );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onSelectRowsOnTemplateGrid(ids = []) {
        this.resetSelection(ids);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    openDataExplorerInstanceEditor(ids, fqn) {
        ids = [].concat(ids);
        let viewName = Registrations.DEIE_VIEW_DETAILS;
        let title = "Template Editor";
        viewName = fqn || viewName;
        const id = ids[0];

        this._explorerTemplate
            .loadExplorerTemplate(this.activeOrgId, id, {name: true})
            .then((template) => {
                title = template.name || title;
                this._gameEditor.launchDataExplorerEditor(
                    {
                        fqn: viewName,
                    },
                    {
                        ids,
                        title,
                    }
                );
            });

        // this.updateExplorerState("openedEditor", { fqn: viewName, id: ids, title });
    }

    loadAndScrollTo(entityId) {
        let that = this;
        this.pagingFeed.on("RequestedEntityFound", handleRequestedEntityFound);
        this.pagingFeed._requestEntity(entityId);

        function handleRequestedEntityFound(index) {
            that.renderer._deviceEntityGrid.renderer.scrollToTheRow(index);
            that.pagingFeed.removeListener("RequestedEntityFound", handleRequestedEntityFound);
        }
    }

    _patchSorting(sorting) {
        // add second sorting order by name
        if (
            sorting &&
            Object.keys(sorting).length === 1 &&
            !sorting.name &&
            this.columnConfig.name
        ) {
            sorting.name = 1;
        }
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    updateExplorerState(property, val) {
        // this._sessionStorage.setData(val, property);
        _.set(this._explorerState, property, val);
    }

    getSelectedPlanIds() {
        return _.get(this._explorerState, "selectedIds", []);
    }

    resetSelection(newIds) {
        let selectedIds = [];

        if (newIds && newIds.length) {
            newIds.forEach((id) => {
                selectedIds.push(id);
            });
        }

        this.renderer._explorerTemplateGrid.setSelectedRows(selectedIds);
        this.updateExplorerState("selectedIds", {selectedRows: selectedIds});
        // this.renderer._renderAvailableActions();
    }

    onUserSorted(sortData) {
        sortData = [].concat(sortData);
        let sort = {};

        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this.columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this.columnConfig[sortData.sortColumn].valuePath ||
                this.columnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;
            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });

        this._patchSorting(sort);

        this._pagingTemplateFeed.setSort(sort);

        this.emit("ResetTitles");
    }

    _onStorageReady() {}

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = DataExplorerGridViewComponent;

DataExplorerGridViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.storageFactory",
    "$teve.solutionsManager",
    "$teve.ui.userFeedback",
    "$teve.entities.ExplorerTemplate",
    "$teve.entities.Explorables",
];

DataExplorerGridViewComponent.$displayDetails = {
    type: "View",
    name: "",
    icon: "common.duplicate",
    shown: false,
    order: 2,
};
